import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

const privilegedProfiles = ['super_admin', 'gdn_admin'];

function FullPageLoading() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0a0a0b', color: '#f97316',
      fontSize: '1.2rem', fontFamily: 'sans-serif', gap: '1rem',
    }}>
      <div style={{
        width: '24px', height: '24px', border: '3px solid #f97316',
        borderTop: '3px solid transparent', borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
      Carregando MecSync...
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export function PrivateRoute({ children, allowedProfiles, skipBillingCheck = false }) {
  const { user, perfil, loading } = useAuth();
  const location = useLocation();
  const [billingState, setBillingState] = useState({ loading: true, blocked: false });

  useEffect(() => {
    async function checkBilling() {
      if (!user || skipBillingCheck || privilegedProfiles.includes(perfil?.perfil)) {
        setBillingState({ loading: false, blocked: false });
        return;
      }

      if (!perfil?.oficina_id) {
        setBillingState({ loading: false, blocked: false });
        return;
      }

      const { data } = await supabase
        .from('assinaturas')
        .select('status_pagamento,data_vencimento,tipo_acesso,trial_fim,bloqueado_em')
        .eq('oficina_id', perfil.oficina_id)
        .maybeSingle();

      const status = String(data?.status_pagamento || 'trial').toLowerCase();
      const type = String(data?.tipo_acesso || '').toLowerCase();
      const due = data?.data_vencimento ? new Date(`${String(data.data_vencimento).split('T')[0]}T00:00:00`) : null;
      const trialEnd = data?.trial_fim ? new Date(`${String(data.trial_fim).split('T')[0]}T23:59:59`) : null;
      const now = new Date();
      const trialExpired = (type === 'trial' || status === 'trial') && trialEnd && now > trialEnd;
      const overdue = due ? now > due : false;
      const blocked = ['bloqueado', 'cancelado', 'suspenso'].includes(status)
        || Boolean(data?.bloqueado_em)
        || trialExpired
        || (['atrasado', 'pendente'].includes(status) && overdue);

      setBillingState({ loading: false, blocked });
    }

    if (!loading) checkBilling();
  }, [user, perfil, loading, skipBillingCheck]);

  if (loading || billingState.loading) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!perfil) return <Navigate to="/login" replace />;
  if (!allowedProfiles?.length && privilegedProfiles.includes(perfil.perfil)) {
    return <Navigate to="/gdn/clientes" replace />;
  }

  if (allowedProfiles?.length && !allowedProfiles.includes(perfil.perfil)) {
    return <Navigate to={privilegedProfiles.includes(perfil.perfil) ? '/gdn/clientes' : '/dashboard'} replace />;
  }

  if (billingState.blocked && location.pathname !== '/configuracoes/pagamento') {
    return <Navigate to="/configuracoes/pagamento" replace />;
  }

  return children;
}
